import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Todo, TodoCategory } from '../../types';

// LOCAL CONTEXT FACTORY - Creates isolated todo list contexts
// Each category gets its own independent todo list state
// This pattern is perfect when you need multiple instances of similar functionality

export interface TodoListContextType {
  // State
  todos: Todo[];
  category: TodoCategory;
  isLoading: boolean;
  filter: 'all' | 'active' | 'completed';
  
  // Actions
  addTodo: (title: string, description?: string, priority?: 'low' | 'medium' | 'high') => void;
  toggleTodo: (id: string) => void;
  deleteTodo: (id: string) => void;
  updateTodo: (id: string, updates: Partial<Pick<Todo, 'title' | 'description' | 'priority'>>) => void;
  setFilter: (filter: 'all' | 'active' | 'completed') => void;
  clearCompleted: () => void;
  
  // Computed values
  filteredTodos: Todo[];
  stats: {
    total: number;
    completed: number;
    active: number;
  };
}

// Factory function that creates a new context for each category
export function createTodoListContext(category: TodoCategory) {
  const TodoListContext = createContext<TodoListContextType | undefined>(undefined);

  function TodoListProvider({ children }: { children: ReactNode }) {
    const [todos, setTodos] = useState<Todo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');

    useEffect(() => {
      setIsLoading(true);
      const storageKey = `todos-${category.id}`;
      const savedTodos = localStorage.getItem(storageKey);
      
      if (savedTodos) {
        try {
          const parsed = JSON.parse(savedTodos);
          const todosWithDates = parsed.map((todo: any) => ({
            ...todo,
            createdAt: new Date(todo.createdAt),
            updatedAt: new Date(todo.updatedAt),
          }));
          setTodos(todosWithDates);
        } catch (error) {
          console.error('Failed to parse todos from localStorage:', error);
        }
      }
      
      setTimeout(() => setIsLoading(false), 500);
    }, [category.id]);

    useEffect(() => {
      if (!isLoading) {
        const storageKey = `todos-${category.id}`;
        localStorage.setItem(storageKey, JSON.stringify(todos));
      }
    }, [todos, category.id, isLoading]);

    const addTodo = useCallback((
      title: string, 
      description?: string, 
      priority: 'low' | 'medium' | 'high' = 'medium'
    ) => {
      const newTodo: Todo = {
        id: `${category.id}-${Date.now()}`,
        title,
        description,
        completed: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        priority,
        category: category.id,
      };
      
      setTodos(prev => [newTodo, ...prev]);
    }, [category.id]);

    const toggleTodo = useCallback((id: string) => {
      setTodos(prev => prev.map(todo => 
        todo.id === id 
          ? { ...todo, completed: !todo.completed, updatedAt: new Date() }
          : todo
      ));
    }, []);

    const deleteTodo = useCallback((id: string) => {
      setTodos(prev => prev.filter(todo => todo.id !== id));
    }, []);

    const updateTodo = useCallback((id: string, updates: Partial<Pick<Todo, 'title' | 'description' | 'priority'>>) => {
      setTodos(prev => prev.map(todo => 
        todo.id === id 
          ? { ...todo, ...updates, updatedAt: new Date() }
          : todo
      ));
    }, []);

    const clearCompleted = useCallback(() => {
      setTodos(prev => prev.filter(todo => !todo.completed));
    }, []);

    const filteredTodos = todos.filter(todo => {
      if (filter === 'active') return !todo.completed;
      if (filter === 'completed') return todo.completed;
      return true;
    });

    const stats = {
      total: todos.length,
      completed: todos.filter(t => t.completed).length,
      active: todos.filter(t => !t.completed).length,
    };

    const value: TodoListContextType = {
      todos,
      category,
      isLoading,
      filter,
      addTodo,
      toggleTodo,
      deleteTodo,
      updateTodo,
      setFilter,
      clearCompleted,
      filteredTodos,
      stats,
    };

    return (
      <TodoListContext.Provider value={value}>
        {children}
      </TodoListContext.Provider>
    );
  }

  function useTodoList(): TodoListContextType {
    const context = useContext(TodoListContext);
    if (!context) {
      throw new Error(`useTodoList must be used within a TodoListProvider for category: ${category.name}`);
    }
    return context;
  }

  return {
    Provider: TodoListProvider,
    useContext: useTodoList,
    Context: TodoListContext,
  };
}

// Why this is LOCAL:
// - Each category (Work, Personal, Shopping) gets its own isolated state
// - Actions in one category don't affect others
// - Each todo list can have different filters, loading states, etc.
// - Perfect for when you need multiple instances of the same functionality
// - Can be dynamically created for new categories

// Usage example:
// const WorkTodoContext = createTodoListContext({ id: 'work', name: 'Work', color: 'blue' });
// const PersonalTodoContext = createTodoListContext({ id: 'personal', name: 'Personal', color: 'green' });
